import { useEffect, useRef, useState } from "react";
import { useMotionPref } from "@/context/MotionPref";
import { motion, useInView } from "framer-motion";
import { Volume2, VolumeX, Maximize2 } from "lucide-react";
import { useMedia } from "@/components/MediaViewer";

const EASE = [0.16, 1, 0.3, 1];

// A vertical reel. Playback and the entrance reveal are both
// driven by framer-motion's useInView on the SIZED wrapper div (observing the
// <video> directly is unreliable — an empty <video> has no layout box). The
// video's <source> elements are attached lazily the first time the tile nears
// the viewport, so a grid of many reels never floods the browser's connections.
export default function Reel({ src, webm, poster, className = "", ratio = "aspect-[3/4]", label, index, reveal = true, sound = false }) {
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [entered, setEntered] = useState(false);
  const { paused } = useMotionPref();
  const inView = useInView(wrapperRef, { margin: "300px 0px", amount: 0.1 });
  const { open } = useMedia();

  const expand = (e) => {
    e.preventDefault();
    e.stopPropagation();
    open({ type: "video", src, poster });
  };

  useEffect(() => {
    if (inView) setEntered(true);
  }, [inView]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (inView) {
      if (!v.dataset.loaded) {
        v.dataset.loaded = "1";
        if (src) {
          const s = document.createElement("source");
          s.src = src;
          s.type = "video/mp4";
          v.appendChild(s);
        }
        if (webm) {
          const w = document.createElement("source");
          w.src = webm;
          w.type = "video/webm";
          v.appendChild(w);
        }
        v.load();
      }
      if (paused) return v.pause();
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      v.pause();
    }
  }, [inView, src, webm, paused]);

  const toggleSound = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    v.play().catch(() => {});
  };

  return (
    <motion.div
      ref={wrapperRef}
      className={className}
      initial={reveal ? { opacity: 0, y: 26 } : false}
      animate={reveal ? (entered ? { opacity: 1, y: 0 } : { opacity: 0, y: 26 }) : undefined}
      transition={{ duration: 1, ease: EASE }}
    >
      <div className={`group relative overflow-hidden bg-paper2 ${ratio}`} data-cursor-hover>
        <video
          ref={videoRef}
          poster={poster}
          muted={muted}
          loop
          playsInline
          preload="none"
          className="hover-media h-full w-full object-cover"
        />
        <button
          type="button"
          onClick={expand}
          data-testid="reel-expand"
          aria-label="Open video"
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-paper/80 text-ink opacity-0 backdrop-blur-sm transition-opacity duration-300 hover:bg-paper group-hover:opacity-100"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
        {sound && (
          <button
            type="button"
            onClick={toggleSound}
            data-testid="reel-sound-toggle"
            aria-label={muted ? "Unmute" : "Mute"}
            className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full bg-paper/80 px-3 py-1.5 text-ink backdrop-blur-md transition-colors duration-300 hover:bg-paper"
          >
            {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
            <span className="overline text-[0.55rem]">{muted ? "Sound" : "On"}</span>
          </button>
        )}
      </div>
    </motion.div>
  );
}
